/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DiagnosticLogger } from "./DiagnosticLogger";
import { Log } from "./Log";
import type { LogDetails } from "../types";
import { DiagnosticSeverity } from "../types";

/** Caller-supplied Emergency Alert System (EAS) telemetry fields. */
export type EasLogFields = Record<string, string | undefined>;

const EVENT_FLOW: string = "eas_flow";

/** Prefix for {@link EasLogger.nextAlertId}, so an id is recognisable in a log. */
const ALERT_ID_PREFIX: string = "eas-";

const TAG: string = "EasLogger";

const KEY_EVENT: string = "event";
const KEY_ALERT_ID: string = "alert_id";
const KEY_ERROR_TYPE: string = "error_type";
const UNKNOWN_ERROR_TYPE: string = "unknown";

/**
 * Stages of one Emergency Alert System alert, in the order a healthy alert
 * passes through them.
 *
 * Reading a session's `eas_flow` events in order reconstructs the whole life
 * of an alert: what arrived, what it displaced, what was decided about it,
 * what the viewer saw, how it ended, and what was put back. The gaps are the
 * diagnosis — an alert with `alert_received` and no `alert_displayed` never
 * reached a screen; one with `alert_finished` and no `playback_restored` left
 * the viewer on a paused player.
 */
export const EasFlowEvent = {
  /** A parsed alert reached the model. */
  AlertReceived: "alert_received",
  /** Refused at the door — an alert type this app does not display. */
  AlertIgnored: "alert_ignored",
  /** This alert replaced one already on screen. */
  AlertSuperseded: "alert_superseded",
  /** The viewer's playback state was captured, muted and paused. */
  PlaybackPreempted: "playback_preempted",
  /** Preemption could not run; the alert displays over running content. */
  PlaybackPreemptionFailed: "playback_preemption_failed",
  /** Market template and `allowUserExit` combined into one answer. */
  DismissibilityResolved: "dismissibility_resolved",
  /** Every input except the dismiss key is now swallowed. */
  InputBlocked: "input_blocked",
  /** A listener accepted the alert and put it on screen. */
  AlertDisplayed: "alert_displayed",
  /** Nothing displayed it; the alert is abandoned rather than played blind. */
  AlertNotDisplayed: "alert_not_displayed",
  /** The route the alert interrupted was recorded for the restore. */
  RouteRecorded: "route_recorded",
  /** The alert's own media was asked to start. */
  AlertMediaStarted: "alert_media_started",
  /** The alert's media reported first frame / first sample. */
  AlertMediaPlaying: "alert_media_playing",
  /** End of media — the alert's own way out. */
  AlertMediaEnded: "alert_media_ended",
  /** The alert's media failed; the text stays on screen regardless. */
  AlertMediaFailed: "alert_media_failed",
  /** The fallback expiry clock was armed, and why. */
  ExpiryArmed: "expiry_armed",
  /** The fallback expiry clock ran out and ended the alert. */
  ExpiryFired: "expiry_fired",
  /** The viewer pressed the dismiss key on a dismissible alert. */
  DismissRequested: "dismiss_requested",
  /** Input routing returned to normal. */
  InputReleased: "input_released",
  /** The alert finished, for whatever reason. */
  AlertFinished: "alert_finished",
  /** The viewer was returned to the screen the alert interrupted. */
  RouteRestored: "route_restored",
  /** Content was unmuted and resumed. */
  PlaybackRestored: "playback_restored",
  /** Content was deliberately left as it was found. */
  PlaybackLeftAsFound: "playback_left_as_found"
} as const;

export type EasFlowEventName = (typeof EasFlowEvent)[keyof typeof EasFlowEvent];

/**
 * Structured telemetry facade for the Emergency Alert System flow.
 *
 * Modelled on `PostSigninLogger`'s diagnostic half: `DiagnosticLogger` carries
 * every stage under one event name, and nothing else does.
 *
 * Deliberately no operational logging. Operational events are the app's own
 * business transactions — watching content, a streaming session, an API call —
 * and an emergency alert is none of those: it is an interruption *of* them,
 * and whatever it interrupted already has its transaction open and merely
 * paused. Wrapping the alert in a second, overlapping transaction would
 * describe a unit of work the app never performed. The one figure that
 * transaction existed to provide, the alert's duration, is carried on
 * `alert_finished` as `on_screen_ms`.
 *
 * ## The alert's own content is logged; credentials are not
 *
 * The alert text is recorded in full. It is public broadcast content — every
 * receiver in the region is sent the same words — and it is the one field that
 * answers the questions these logs exist for: whether the OSD was blank
 * because nothing arrived or because the text was lost on the way, and whether
 * what the viewer read was what the broadcaster sent. `text_length` is kept
 * alongside it so an empty alert is still greppable.
 *
 * The media URL is recorded through {@link EasLogger.sanitizeUrl}, which drops
 * the query string. Alert media is commonly served from a CDN behind a signed
 * URL, and the signature lives in that query — it is a credential, and
 * diagnostics leave the device for the telemetry backhaul. Origin and path are
 * what a 404 or a wrong-CDN investigation actually needs.
 *
 * Nothing else about the viewer goes in. `PostSigninLogger`'s rule still holds
 * for tokens, device and profile identifiers, and raw error messages.
 *
 * ## Correlation
 *
 * Every alert takes an id from {@link EasLogger.nextAlertId}, threaded through
 * each stage as `alert_id`. A burst of alerts interleaves in the log; without
 * it, a superseded alert's teardown is indistinguishable from its successor's.
 */
export class EasLogger {
  /** Source of {@link nextAlertId}; session-local, reset only by a reload. */
  private static mAlertCounter: number = 0;

  /**
   * Mint a correlation id for one alert.
   *
   * Session-local and monotonic — enough to group one alert's stages within a
   * session's logs, which is all `alert_id` is for. It deliberately carries no
   * device or session identifier: those belong to the telemetry envelope, not
   * to a field this flow sets.
   *
   * @returns The id to thread through this alert's events
   */
  public static nextAlertId(): string {
    EasLogger.mAlertCounter += 1;
    return ALERT_ID_PREFIX + String(EasLogger.mAlertCounter);
  }

  /**
   * Reduce a media URL to the part that is safe to record.
   *
   * Keeps the origin and path, drops the query and fragment. Alert media is
   * commonly behind a signed CDN URL whose signature is a query parameter, and
   * a diagnostic is backhauled off the device — so the query is treated as a
   * credential and never recorded. What remains is what a 404, a wrong-CDN or
   * a wrong-extension investigation needs.
   *
   * A value that is not an absolute URL — a root-relative path, or something
   * that is not a URL at all — is truncated at the first `?` or `#` instead.
   * It is kept rather than discarded, because a `mediaUrl` that is not a URL
   * is itself the finding, and truncating still guarantees no query survives.
   *
   * @param url - The raw media URL; "" when the alert carries none
   * @returns Origin and path, a truncated path, or "" for no URL
   */
  public static sanitizeUrl(url: string): string {
    if (url.length === 0) {
      return "";
    }
    try {
      const parsed: URL = new URL(url);
      return parsed.origin + parsed.pathname;
    } catch {
      const markerIndex: number = url.search(/[?#]/);
      return markerIndex === -1 ? url : url.slice(0, markerIndex);
    }
  }

  /**
   * Emit one stage of the alert's life.
   *
   * @param event - Which stage
   * @param message - Human-readable summary, free of alert content
   * @param severity - Diagnostic severity
   * @param alertId - Correlation id from {@link nextAlertId}, when known
   * @param extra - Additional pre-redacted fields
   */
  public static logFlowEvent(
    event: EasFlowEventName,
    message: string,
    severity: DiagnosticSeverity,
    alertId?: string,
    extra?: EasLogFields
  ): void {
    const details: LogDetails = { [KEY_EVENT]: event };
    if (alertId !== undefined && alertId.length > 0) {
      details[KEY_ALERT_ID] = alertId;
    }
    EasLogger.assign(details, extra);
    EasLogger.emit((): void => {
      DiagnosticLogger.log(EVENT_FLOW, message, severity, details);
    });
  }

  /**
   * Emit a stage that failed, with the error classified rather than quoted.
   *
   * @param event - Which stage
   * @param message - Human-readable summary, free of alert content
   * @param error - The thrown value; only its type is recorded
   * @param alertId - Correlation id from {@link nextAlertId}, when known
   * @param extra - Additional pre-redacted fields
   */
  public static logFlowFailure(
    event: EasFlowEventName,
    message: string,
    error: unknown,
    alertId?: string,
    extra?: EasLogFields
  ): void {
    const details: LogDetails = {
      [KEY_ERROR_TYPE]: EasLogger.describeErrorType(error)
    };
    EasLogger.assign(details, extra);
    EasLogger.logFlowEvent(
      event,
      message,
      DiagnosticSeverity.Error,
      alertId,
      details
    );
  }

  /**
   * Redacted error classification for telemetry.
   *
   * @param error - The thrown value
   * @returns The constructor name, or "unknown" for a non-Error throw
   */
  public static describeErrorType(error: unknown): string {
    if (error instanceof Error) {
      return error.constructor.name;
    }
    return UNKNOWN_ERROR_TYPE;
  }

  /**
   * Run one telemetry emit, swallowing anything it throws.
   *
   * These calls sit inline on the path that puts an emergency alert on the
   * screen, blocks input and gives the screen back. A telemetry backend that
   * is misconfigured, half-initialised or simply absent must not be able to
   * stop any of that — an alert the viewer never sees is a far worse outcome
   * than a log line nobody writes. The failure is reported through `Log` so
   * it is still visible in a console session.
   *
   * @param write - The emit to attempt
   */
  private static emit(write: () => void): void {
    try {
      write();
    } catch (error: unknown) {
      Log.e(TAG, "emit() — EAS telemetry failed, continuing", error);
    }
  }

  private static assign(target: LogDetails, extra?: EasLogFields): void {
    if (extra === undefined || extra === null) {
      return;
    }
    const keys: string[] = Object.keys(extra);
    for (let index = 0; index < keys.length; index += 1) {
      const value: string | undefined = extra[keys[index]];
      if (value !== undefined) {
        target[keys[index]] = value;
      }
    }
  }
}
